import React, {Fragment} from 'react';
import {kebabCase, uniqueId} from 'lodash';
import classNames from 'classnames';
import {useHistory, useLocation} from 'react-router-dom';

import {Course} from 'src/types/store';
import {gpaFormat} from 'shared/utils';
import {useMediaQuery} from 'src/hooks/useMediaQuery';

import SchoolHeader from './school-header';
import {getGradeSettingHeaders, getGradeRow} from './utils';
import styles from './school-section.module.scss';

interface Props {
  schoolName: string;
  courseGroups: Record<string, Course[]>;
  hideSchoolName?: boolean;
}

const SchoolSection = ({schoolName, courseGroups, hideSchoolName}: Props) => {
  const history = useHistory();
  const {pathname} = useLocation();
  const kebabSchoolName = kebabCase(schoolName);
  const coursesByGradeSettings = Object.keys(courseGroups);
  const mq = useMediaQuery();

  if (mq.MD_AND_UP) {
    return (
      <table className={classNames(styles.table, 'shared-bulk-entry-school-table')}>
        {!hideSchoolName && (
          <caption className={styles.caption}>
            <SchoolHeader>{schoolName}</SchoolHeader>
          </caption>
        )}

        {coursesByGradeSettings.map((gradeSettingsStr) => {
          const gradeSettings = JSON.parse(gradeSettingsStr);
          const courses = courseGroups[gradeSettingsStr];
          const gradeSettingHeaders = getGradeSettingHeaders(gradeSettings);

          if (!courses.length) return null;

          return (
            <Fragment key={gradeSettingsStr}>
              <thead className={styles.header}>
                <tr>
                  <th className={styles.courseNameHeader}>Course Name</th>

                  {gradeSettingHeaders.map((header, i) => (
                    <th
                      className={styles.gradeCell}
                      key={`${kebabSchoolName}-${uniqueId()}-grade-header-${i}`}
                    >
                      {header}
                    </th>
                  ))}

                  <th className={styles.finalGrade}>
                    {gradeSettings.hasFinalGrade ? 'Final Grade' : ''}
                  </th>

                  <th className={styles.credits}>Credits</th>
                </tr>
              </thead>

              <tbody>
                {courses.map((course) => {
                  const gradeRow = getGradeRow(course, gradeSettings);

                  return (
                    <tr key={course._id} className={styles.bodyRow}>
                      <td
                        className={classNames(
                          styles.courseNameRow,
                          'shared-bulk-entry-school-table-course-name-row',
                          {[styles.core]: course.core}
                        )}
                      >
                        <span
                          className={styles.courseName}
                          dangerouslySetInnerHTML={{__html: course.name}}
                          title={course.name}
                        />
                      </td>

                      {gradeRow.map((row, i) => (
                        <td key={`${kebabSchoolName}-${uniqueId()}-grade-row-${i}`}>{row}</td>
                      ))}

                      <td>{gradeSettings.hasFinalGrade ? course.grades.at(-1)?.gradeInput : ''}</td>

                      <td>{gpaFormat(course.awardableCredits)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Fragment>
          );
        })}
      </table>
    );
  } else {
    return coursesByGradeSettings.map((gradeSettingsStr) => {
      const gradeSettings = JSON.parse(gradeSettingsStr);
      const courses = courseGroups[gradeSettingsStr];
      const gradeSettingHeaders = getGradeSettingHeaders(gradeSettings);

      if (!courses.length) return null;

      return courses.map((course) => {
        const gradeRow = getGradeRow(course, gradeSettings);

        return (
          <table
            key={course._id}
            className={classNames(
              styles.table,
              styles.mobile,
              'shared-bulk-entry-school-table-mobile',
              {[styles.core]: course.core}
            )}
            onClick={() =>
              history.replace({
                pathname: `${pathname}/course/${course._id}`,
                state: {skipScrolling: true},
              })
            }
          >
            <caption
              className={classNames(styles.courseNameHeader, styles.mobile)}
              dangerouslySetInnerHTML={{__html: course.name}}
              title={course.name}
            />

            <thead className={styles.header}>
              <tr>
                {gradeSettingHeaders.map((header, i) => (
                  <th
                    className={styles.mobileCell}
                    key={`${kebabSchoolName}-${uniqueId()}-grade-header-${i}`}
                  >
                    {header}
                  </th>
                ))}

                <th className={styles.mobileCell}>{gradeSettings.hasFinalGrade ? 'Final' : ''}</th>

                <th className={styles.mobileCell}>Credits</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                {gradeRow.map((row, i) => (
                  <td key={`${kebabSchoolName}-${uniqueId()}-grade-row-${i}`}>{row}</td>
                ))}

                <td>{gradeSettings.hasFinalGrade ? course.grades.at(-1)?.gradeInput : ''}</td>

                <td>{gpaFormat(course.awardableCredits)}</td>
              </tr>
            </tbody>
          </table>
        );
      });
    });
  }
};

export default SchoolSection;
